/*
 * Назначение: страница «Профиль» (мой аккаунт).
 * Функционал: показывает данные текущего пользователя, позволяет изменить
 * ФИО и сменить пароль с подтверждением текущего. Все запросы — через
 * /api/admin/auth/me.
 */

import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  AlertCircle,
  CheckCircle2,
  KeyRound,
  Loader2,
  Save,
  ShieldCheck,
  User as UserIcon,
} from "lucide-react";
import { toast } from "sonner";

import { ApiError } from "../../lib/api";
import { authApi } from "../../lib/auth";
import { useAuth } from "../components/auth/auth-provider";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Separator } from "../components/ui/separator";

const ROLE_LABEL: Record<string, string> = {
  owner: "Владелец",
  admin: "Администратор",
  manager: "Менеджер",
  viewer: "Наблюдатель",
};

export function ProfilePage() {
  const { user, setUser } = useAuth();

  const [fullName, setFullName] = useState(user?.full_name ?? "");
  const [email, setEmail] = useState(user?.email ?? "");
  const [profileError, setProfileError] = useState<string | null>(null);

  useEffect(() => {
    setFullName(user?.full_name ?? "");
    setEmail(user?.email ?? "");
  }, [user]);

  const profileMutation = useMutation({
    mutationFn: () =>
      authApi.updateMe({
        full_name: fullName.trim() ? fullName.trim() : null,
        email: email.trim() ? email.trim() : null,
      }),
    onSuccess: (updated) => {
      setUser(updated);
      toast.success("Профиль обновлён");
    },
    onError: (err) => {
      setProfileError(
        err instanceof ApiError ? err.detail : "Не удалось сохранить профиль",
      );
    },
  });

  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [repeatPwd, setRepeatPwd] = useState("");
  const [pwdError, setPwdError] = useState<string | null>(null);

  const passwordMutation = useMutation({
    mutationFn: () => authApi.changePassword(currentPwd, newPwd),
    onSuccess: () => {
      toast.success("Пароль обновлён");
      setCurrentPwd("");
      setNewPwd("");
      setRepeatPwd("");
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        if (err.status === 401) setPwdError("Неверный текущий пароль");
        else setPwdError(err.detail || "Не удалось сменить пароль");
      } else {
        setPwdError("Сервер недоступен");
      }
    },
  });

  if (!user) {
    return null;
  }

  const handleChangePassword = () => {
    setPwdError(null);
    if (!currentPwd) return setPwdError("Укажи текущий пароль");
    if (newPwd.length < 8) return setPwdError("Новый пароль не короче 8 символов");
    if (newPwd !== repeatPwd) return setPwdError("Новый пароль и повтор не совпадают");
    if (newPwd === currentPwd)
      return setPwdError("Новый пароль должен отличаться от текущего");
    passwordMutation.mutate();
  };

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6">
      <div>
        <h2 className="text-2xl font-semibold">Профиль</h2>
        <p className="text-sm text-muted-foreground">
          Личные данные и безопасность твоей учётной записи.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserIcon className="h-5 w-5" />
            Учётная запись
          </CardTitle>
          <CardDescription>
            Логин и роль изменить нельзя — это делает владелец системы в
            разделе «Пользователи».
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Логин</Label>
              <Input value={user.login} readOnly disabled className="font-mono" />
            </div>
            <div className="space-y-2">
              <Label>Роль</Label>
              <div>
                <Badge variant={user.role === "owner" ? "default" : "outline"}>
                  {ROLE_LABEL[user.role] ?? user.role}
                </Badge>
              </div>
            </div>
          </div>

          <Separator />

          <div className="space-y-2">
            <Label htmlFor="profile-name">ФИО / отображаемое имя</Label>
            <Input
              id="profile-name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Анна Сидорова"
              maxLength={128}
            />
            <p className="text-xs text-muted-foreground">
              Это имя видят другие пользователи админки.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="profile-email">Почта для кодов входа</Label>
            <Input
              id="profile-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="почта@домен.ру"
              maxLength={255}
              autoComplete="email"
            />
            <p className="text-xs text-muted-foreground">
              На этот адрес приходит код подтверждения входа и письма о попытках
              подобрать пароль. Без адреса подтверждение входа не включить.
            </p>
          </div>

          {profileError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{profileError}</AlertDescription>
            </Alert>
          )}

          <Button
            onClick={() => {
              setProfileError(null);
              profileMutation.mutate();
            }}
            disabled={
              profileMutation.isPending ||
              (fullName.trim() === (user.full_name ?? "").trim() &&
                email.trim() === (user.email ?? "").trim())
            }
          >
            {profileMutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            Сохранить
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5" />
            Смена пароля
          </CardTitle>
          <CardDescription>
            Подтверди текущий пароль, затем задай новый. Минимум 8 символов.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="pwd-current">Текущий пароль</Label>
            <Input
              id="pwd-current"
              type="password"
              autoComplete="current-password"
              value={currentPwd}
              onChange={(e) => setCurrentPwd(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="pwd-new">Новый пароль</Label>
              <Input
                id="pwd-new"
                type="password"
                autoComplete="new-password"
                value={newPwd}
                onChange={(e) => setNewPwd(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pwd-repeat">Повтори новый пароль</Label>
              <Input
                id="pwd-repeat"
                type="password"
                autoComplete="new-password"
                value={repeatPwd}
                onChange={(e) => setRepeatPwd(e.target.value)}
              />
            </div>
          </div>

          {pwdError ? (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{pwdError}</AlertDescription>
            </Alert>
          ) : (
            <Alert>
              <ShieldCheck className="h-4 w-4" />
              <AlertDescription>
                После смены пароля сессия не разрывается — можно продолжать работу.
              </AlertDescription>
            </Alert>
          )}

          <Button
            variant="default"
            onClick={handleChangePassword}
            disabled={passwordMutation.isPending}
          >
            {passwordMutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <CheckCircle2 className="mr-2 h-4 w-4" />
            )}
            Сменить пароль
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
